import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { GeneralLedgerVouchersService } from "services/general-ledger-vouchers";
import { formatVndIntegerValue } from "utils";
import toast from "react-hot-toast";

const currentDate = new Date();
const defaultYear = String(currentDate.getFullYear());
const defaultMonth = String(currentDate.getMonth() + 1).padStart(2, "0");

const ui = {
  title: { fontSize: 20, lineHeight: 1.2 },
  label: { fontSize: 14, lineHeight: 1.2 },
  table: { fontSize: 13 },
  th: { border: "1px solid #000", padding: 6, textAlign: "center", fontSize: 13 },
  tdLeft: { border: "1px solid #000", padding: 6, textAlign: "left", fontSize: 13 },
  tdCenter: { border: "1px solid #000", padding: 6, textAlign: "center", fontSize: 13 },
  tdRight: { border: "1px solid #000", padding: 6, textAlign: "right", fontSize: 13 },
};

const formatMoney = (value) => formatVndIntegerValue(value || 0);

const buildPrintTemplate = ({ unit, companyAddress, account, filters, rows, summary }) => {
  const renderAmount = (value) => new Intl.NumberFormat("vi-VN").format(Number(value || 0));

  const tableRows = rows
    .map(
      (item) => `
      <tr>
        <td class="cell center">${item.postingDate || ""}</td>
        <td class="cell center">${item.voucherNumber || ""}</td>
        <td class="cell center">${item.voucherDate || ""}</td>
        <td class="cell left">${item.description || ""}</td>
        <td class="cell center">${item.correspondingAccount || ""}</td>
        <td class="cell right">${item.debitAmount ? renderAmount(item.debitAmount) : ""}</td>
        <td class="cell right">${item.creditAmount ? renderAmount(item.creditAmount) : ""}</td>
        <td class="cell left">${item.note || ""}</td>
      </tr>
    `
    )
    .join("");

  const summaryRow = (label, balance, isBalance = false) => `
    <tr>
      <td class="cell left" colspan="5"><strong>- ${label}</strong></td>
      <td class="cell right"><strong>${isBalance ? (balance.debit ? renderAmount(balance.debit) : "x") : renderAmount(balance.debit)}</strong></td>
      <td class="cell right"><strong>${isBalance ? (balance.credit ? renderAmount(balance.credit) : "x") : renderAmount(balance.credit)}</strong></td>
      <td class="cell left"></td>
    </tr>
  `;

  return `
  <html>
    <head>
      <meta charset="UTF-8" />
      <title>Sổ Cái S02c1-DN</title>
      <style>
        body { font-family: 'Times New Roman', serif; color: #000; margin: 24px; font-size: 14px; }
        .header { display: flex; justify-content: space-between; margin-bottom: 12px; }
        .center { text-align: center; }
        .left { text-align: left; }
        .right { text-align: right; }
        table { width: 100%; border-collapse: collapse; }
        .cell, .head { border: 1px solid #000; padding: 6px; vertical-align: middle; }
        .head { font-weight: 700; text-align: center; }
        .title { text-align: center; margin: 10px 0 16px; }
        .title h1 { margin: 0; font-size: 26px; text-transform: uppercase; }
        .title p { margin: 4px 0; }
        .meta { margin-bottom: 10px; }
        .sign { margin-top: 48px; display: flex; justify-content: space-between; align-items: flex-start; text-align: center; }
        .sign-col { width: 32%; }
        .sign-date-row { min-height: 20px; margin-bottom: 2px; }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <div><strong>Đơn vị:</strong> ${unit || "......................................"}</div>
          <div><strong>Địa chỉ:</strong> ${companyAddress || "......................................"}</div>
        </div>
        <div class="center">
          <div><strong>Mẫu số S02c1-DN</strong></div>
          <div><em>(Kèm theo Thông tư số 99/2025/TT-BTC ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</em></div>
        </div>
      </div>
      <div class="title">
        <h1>Sổ Cái</h1>
        <p><strong>(Dùng cho hình thức kế toán Chứng từ ghi sổ)</strong></p>
        <p>Năm: ${filters.year}</p>
        <p>Tên tài khoản: ${account.accountName || ""}</p>
        <p>Số hiệu: ${account.accountCode || ""}</p>
      </div>
      <table>
        <thead>
          <tr>
            <th class="head" rowspan="2">Ngày, tháng ghi sổ<br/>A</th>
            <th class="head" colspan="2">Chứng từ ghi sổ</th>
            <th class="head" rowspan="2">Diễn giải<br/>D</th>
            <th class="head" rowspan="2">Số hiệu TK đối ứng<br/>E</th>
            <th class="head" colspan="2">Số tiền</th>
            <th class="head" rowspan="2">Ghi chú<br/>G</th>
          </tr>
          <tr>
            <th class="head">Số hiệu<br/>B</th>
            <th class="head">Ngày, tháng<br/>C</th>
            <th class="head">Nợ<br/>1</th>
            <th class="head">Có<br/>2</th>
          </tr>
        </thead>
        <tbody>
          ${summaryRow("Số dư đầu năm", summary.openingBalance, true)}
          <tr>
            <td class="cell left" colspan="8"><strong>- Số phát sinh trong tháng</strong></td>
          </tr>
          ${tableRows || '<tr><td class="cell center" colspan="8">Không có dữ liệu</td></tr>'}
          <tr>
            <td class="cell left" colspan="5"><strong>- Cộng số phát sinh tháng</strong></td>
            <td class="cell right"><strong>${renderAmount(summary.monthlyDebitTotal)}</strong></td>
            <td class="cell right"><strong>${renderAmount(summary.monthlyCreditTotal)}</strong></td>
            <td class="cell left"></td>
          </tr>
          ${summaryRow("Số dư cuối tháng", summary.closingBalance, true)}
          <tr>
            <td class="cell left" colspan="5"><strong>- Cộng lũy kế từ đầu quý</strong></td>
            <td class="cell right"><strong>${renderAmount(summary.quarterAccumulatedDebit)}</strong></td>
            <td class="cell right"><strong>${renderAmount(summary.quarterAccumulatedCredit)}</strong></td>
            <td class="cell left"></td>
          </tr>
        </tbody>
      </table>
      <div class="sign">
        <div class="sign-col">
          <div class="sign-date-row">&nbsp;</div>
          <div><strong>Người ghi sổ</strong></div>
          <div><em>(Ký, họ tên)</em></div>
        </div>
        <div class="sign-col">
          <div class="sign-date-row">&nbsp;</div>
          <div><strong>Kế toán trưởng</strong></div>
          <div><em>(Ký, họ tên)</em></div>
        </div>
        <div class="sign-col">
          <div class="sign-date-row"><em>Ngày ...... tháng ...... năm ......</em></div>
          <div><strong>Người đại diện theo pháp luật</strong></div>
          <div><em>(Ký, họ tên, đóng dấu)</em></div>
        </div>
      </div>
    </body>
  </html>`;
};

export default function GeneralLedgerDetail({ baseRoute = "/templates/general-ledger" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    year: defaultYear,
    month: defaultMonth,
    accountCode: "",
    searchBy: "",
  });
  const [account, setAccount] = useState({ accountCode: "", accountName: "" });
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    year: Number(defaultYear),
    month: Number(defaultMonth),
    quarter: 1,
    openingBalance: { debit: 0, credit: 0 },
    monthlyDebitTotal: 0,
    monthlyCreditTotal: 0,
    closingBalance: { debit: 0, credit: 0 },
    quarterAccumulatedDebit: 0,
    quarterAccumulatedCredit: 0,
    yearAccumulatedDebit: 0,
    yearAccumulatedCredit: 0,
  });
  const [unit, setUnit] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [paginate, setPaginate] = useState({ page: 1, size: 1000, count: 0 });
  const [reconciliation, setReconciliation] = useState({
    totalDebit: 0,
    totalCredit: 0,
    difference: 0,
    isBalanced: true,
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || "");
    const year = searchParams.get("year") || defaultYear;
    const month = searchParams.get("month") || defaultMonth;
    const accountCode = searchParams.get("accountCode") || "";
    const searchBy = searchParams.get("searchBy") || "";

    setFilters({ year, month, accountCode, searchBy });
  }, [location.search]);

  const fetchData = useCallback(async () => {
    if (!filters.accountCode) return;

    try {
      setLoading(true);
      const response = await GeneralLedgerVouchersService.getPaginate({
        ...filters,
        page: 1,
        limit: 1000,
        sortBy: "voucherDate",
        sortType: "asc",
      });
      const payload = response?.data || response || {};

      setAccount(payload.account || { accountCode: "", accountName: "" });
      setRows(payload.items || []);
      setSummary(
        payload.summary || {
          year: Number(filters.year),
          month: Number(filters.month),
          quarter: 1,
          openingBalance: { debit: 0, credit: 0 },
          monthlyDebitTotal: 0,
          monthlyCreditTotal: 0,
          closingBalance: { debit: 0, credit: 0 },
          quarterAccumulatedDebit: 0,
          quarterAccumulatedCredit: 0,
          yearAccumulatedDebit: 0,
          yearAccumulatedCredit: 0,
        }
      );
      setReconciliation(
        payload.reconciliation || {
          totalDebit: 0,
          totalCredit: 0,
          difference: 0,
          isBalanced: true,
        }
      );
      setPaginate(payload.paginate || { page: 1, size: 1000, count: 0 });
      setUnit(payload.unit || "");
      setCompanyAddress(payload.companyAddress || "");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Không thể tải Sổ Cái");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = useMemo(() => {
    const size = Number(paginate.size || 1);
    const count = Number(paginate.count || 0);
    return Math.max(1, Math.ceil(count / size));
  }, [paginate.count, paginate.size]);

  const handlePrint = () => {
    const htmlContent = buildPrintTemplate({
      unit,
      companyAddress,
      account,
      filters,
      rows,
      summary,
    });
    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      toast.error("Trình duyệt đang chặn cửa sổ in. Vui lòng cho phép pop-up.");
      return;
    }

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 200);
  };

  const renderBalanceValue = (balance, side) => {
    const amount = Number(balance?.[side] || 0);
    return amount ? formatMoney(amount) : "x";
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={11}>
            <Card>
              <SoftBox p={3} display="flex" flexDirection="column" minHeight="calc(100vh - 220px)">
                <SoftBox mb={2}>
                  <SoftTypography variant="h5" fontWeight="bold" sx={ui.title}>
                    Sổ Cái (S02c1-DN)
                  </SoftTypography>
                </SoftBox>

                <SoftBox
                  border="1px solid #000"
                  p={3}
                  sx={{ fontFamily: "'Times New Roman', serif", color: "#000" }}
                >
                  <SoftBox display="flex" justifyContent="space-between" mb={2}>
                    <SoftBox>
                      <div style={{ fontSize: 14 }}>
                        <strong>Đơn vị:</strong> {unit || "......................................"}
                      </div>
                      <div style={{ fontSize: 14 }}>
                        <strong>Địa chỉ:</strong> {companyAddress || "......................................"}
                      </div>
                    </SoftBox>
                    <SoftBox textAlign="center" width="48%">
                      <div style={{ fontSize: 14, fontWeight: 700 }}>Mẫu số S02c1-DN</div>
                      <div style={{ fontSize: 14, fontStyle: "italic" }}>
                        (Kèm theo Thông tư số 99/2025/TT-BTC ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)
                      </div>
                    </SoftBox>
                  </SoftBox>

                  <SoftBox textAlign="center" mb={2}>
                    <div style={{ fontSize: 28, fontWeight: 700, textTransform: "uppercase" }}>Sổ Cái</div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>(Dùng cho hình thức kế toán Chứng từ ghi sổ)</div>
                    <div style={{ fontSize: 14, marginTop: 6 }}>Năm: {filters.year}</div>
                    <div style={{ fontSize: 14 }}>Tên tài khoản: {account.accountName || ""}</div>
                    <div style={{ fontSize: 14 }}>Số hiệu: {account.accountCode || ""}</div>
                  </SoftBox>

                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100, ...ui.table }}>
                      <thead>
                        <tr>
                          <th style={ui.th} rowSpan={2}>Ngày, tháng ghi sổ<br />A</th>
                          <th style={ui.th} colSpan={2}>Chứng từ ghi sổ</th>
                          <th style={ui.th} rowSpan={2}>Diễn giải<br />D</th>
                          <th style={ui.th} rowSpan={2}>Số hiệu TK đối ứng<br />E</th>
                          <th style={ui.th} colSpan={2}>Số tiền</th>
                          <th style={ui.th} rowSpan={2}>Ghi chú<br />G</th>
                        </tr>
                        <tr>
                          <th style={ui.th}>Số hiệu<br />B</th>
                          <th style={ui.th}>Ngày, tháng<br />C</th>
                          <th style={ui.th}>Nợ<br />1</th>
                          <th style={ui.th}>Có<br />2</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ ...ui.tdLeft, fontWeight: 700 }} colSpan={5}>
                            - Số dư đầu năm
                          </td>
                          <td style={{ ...ui.tdRight, fontWeight: 700 }}>
                            {renderBalanceValue(summary.openingBalance, "debit")}
                          </td>
                          <td style={{ ...ui.tdRight, fontWeight: 700 }}>
                            {renderBalanceValue(summary.openingBalance, "credit")}
                          </td>
                          <td style={ui.tdLeft}></td>
                        </tr>
                        <tr>
                          <td style={{ ...ui.tdLeft, fontWeight: 700 }} colSpan={8}>
                            - Số phát sinh trong tháng
                          </td>
                        </tr>

                        {loading && (
                          <tr>
                            <td style={ui.tdCenter} colSpan={8}>
                              Đang tải dữ liệu...
                            </td>
                          </tr>
                        )}

                        {!loading && !rows.length && (
                          <tr>
                            <td style={ui.tdCenter} colSpan={8}>
                              Không có dữ liệu
                            </td>
                          </tr>
                        )}

                        {!loading &&
                          rows.map((item) => (
                            <tr
                              key={item.id}
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                navigate(`/templates/accounting-vouchers/${item.voucherId}/view`)
                              }
                            >
                              <td style={ui.tdCenter}>{item.postingDate}</td>
                              <td style={ui.tdCenter}>{item.voucherNumber}</td>
                              <td style={ui.tdCenter}>{item.voucherDate}</td>
                              <td style={ui.tdLeft}>{item.description}</td>
                              <td style={ui.tdCenter}>{item.correspondingAccount}</td>
                              <td style={ui.tdRight}>
                                {item.debitAmount ? formatMoney(item.debitAmount) : ""}
                              </td>
                              <td style={ui.tdRight}>
                                {item.creditAmount ? formatMoney(item.creditAmount) : ""}
                              </td>
                              <td style={ui.tdLeft}>{item.note || ""}</td>
                            </tr>
                          ))}

                        <tr>
                          <td style={{ ...ui.tdLeft, fontWeight: 700 }} colSpan={5}>
                            - Cộng số phát sinh tháng
                          </td>
                          <td style={{ ...ui.tdRight, fontWeight: 700 }}>
                            {formatMoney(summary.monthlyDebitTotal)}
                          </td>
                          <td style={{ ...ui.tdRight, fontWeight: 700 }}>
                            {formatMoney(summary.monthlyCreditTotal)}
                          </td>
                          <td style={ui.tdLeft}></td>
                        </tr>
                        <tr>
                          <td style={{ ...ui.tdLeft, fontWeight: 700 }} colSpan={5}>
                            - Số dư cuối tháng
                          </td>
                          <td style={{ ...ui.tdRight, fontWeight: 700 }}>
                            {renderBalanceValue(summary.closingBalance, "debit")}
                          </td>
                          <td style={{ ...ui.tdRight, fontWeight: 700 }}>
                            {renderBalanceValue(summary.closingBalance, "credit")}
                          </td>
                          <td style={ui.tdLeft}></td>
                        </tr>
                        <tr>
                          <td style={{ ...ui.tdLeft, fontWeight: 700 }} colSpan={5}>
                            - Cộng lũy kế từ đầu quý
                          </td>
                          <td style={{ ...ui.tdRight, fontWeight: 700 }}>
                            {formatMoney(summary.quarterAccumulatedDebit)}
                          </td>
                          <td style={{ ...ui.tdRight, fontWeight: 700 }}>
                            {formatMoney(summary.quarterAccumulatedCredit)}
                          </td>
                          <td style={ui.tdLeft}></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <SoftBox mt={2} display="flex" justifyContent="space-between" alignItems="center">
                    <SoftTypography variant="caption" color="text">
                      Tổng số dòng: {paginate.count} {totalPages > 1 ? `- Trang ${paginate.page}/${totalPages}` : ""}
                    </SoftTypography>
                    <span
                      style={{
                        display: "inline-block",
                        borderRadius: 8,
                        padding: "6px 10px",
                        fontSize: 12,
                        fontWeight: 700,
                        color: reconciliation.isBalanced ? "#1b5e20" : "#b71c1c",
                        background: reconciliation.isBalanced ? "#e8f5e9" : "#ffebee",
                      }}
                    >
                      {reconciliation.isBalanced ? "Khớp số liệu" : "Lệch số liệu"}
                    </span>
                  </SoftBox>
                </SoftBox>

                <SoftBox mt="auto" pt={2} display="flex" justifyContent="flex-end" gap={1}>
                  <SoftButton variant="outlined" color="dark" onClick={() => navigate(baseRoute)}>
                    Quay lại
                  </SoftButton>
                  <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                    In Bảng
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
